import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, X, AlertCircle, Sparkles } from "lucide-react";
import { api, errMsg, fmtDateTime, inr } from "@/lib/api";
import { Markdown } from "@/pages/public/Pages";
import { PageTitle, Panel } from "@/components/admin/AdminLayout";
import { ComparisonGenerator, CategoryIntroGenerator, LinkIdeas } from "@/components/admin/AiTools";
import { Switch } from "@/components/ui/switch";

const DraftCard = ({ d, onDone }) => {
  const [c, setC] = useState({ ...d.content, indexable: !(d.content.missing_info || []).length });
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });
  const approve = async () => {
    try { await api.post(`/admin/ai/drafts/${d.id}/approve`, c); toast.success("Published to deal page"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async () => { await api.post(`/admin/ai/drafts/${d.id}/reject`); toast("Draft rejected"); onDone(); };
  const area = "w-full rounded-md border border-slate-200 p-3 text-sm";
  return (
    <Panel title={d.deal_title} testid={`draft-card-${d.id}`} action={<span className="text-xs text-slate-500">{fmtDateTime(d.created_at)} · cost {inr(d.cost_inr)}</span>}>
      {c.missing_info?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900 mb-4 flex gap-2" data-testid="draft-missing-info">
          <AlertCircle size={16} className="shrink-0 mt-0.5" /> <div><b>Missing information:</b> {c.missing_info.join(", ")}. Google indexing is off by default until you verify.</div>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <label className="md:col-span-2 space-y-1 block"><span className="text-xs font-semibold">Description</span><textarea rows={6} className={area} value={c.description} onChange={set("description")} data-testid="draft-description" /></label>
        <label className="space-y-1 block"><span className="text-xs font-semibold">SEO title ({c.seo_title.length}/70)</span><input className={area} value={c.seo_title} onChange={set("seo_title")} maxLength={70} /></label>
        <label className="space-y-1 block"><span className="text-xs font-semibold">Meta description ({c.meta_description.length}/160)</span><input className={area} value={c.meta_description} onChange={set("meta_description")} maxLength={160} /></label>
        <label className="space-y-1 block"><span className="text-xs font-semibold">Social caption</span><textarea rows={3} className={area} value={c.social_caption} onChange={set("social_caption")} /></label>
        <label className="space-y-1 block"><span className="text-xs font-semibold">Image alt text</span><textarea rows={3} className={area} value={c.alt_text} onChange={set("alt_text")} /></label>
      </div>
      {c.faqs?.length > 0 && (
        <div className="mt-4 space-y-2"><p className="text-xs font-semibold">FAQs</p>
          {c.faqs.map((f) => <div key={f.q} className="text-sm bg-slate-50 rounded p-2"><b>{f.q}</b><br />{f.a}</div>)}
        </div>
      )}
      {c.internal_link_keywords?.length > 0 && <p className="text-xs text-slate-500 mt-3">Internal link ideas: {c.internal_link_keywords.join(" · ")}</p>}
      <div className="flex flex-wrap items-center justify-between gap-3 mt-5">
        <label className="flex items-center gap-2 text-sm"><Switch checked={c.indexable} onCheckedChange={(v) => setC({ ...c, indexable: v })} data-testid="draft-indexable-switch" /> I've verified the facts - allow Google indexing</label>
        <div className="flex gap-2">
          <button onClick={reject} data-testid="draft-reject-button" className="px-4 py-2 rounded-lg border flex items-center gap-1"><X size={16} /> Reject</button>
          <button onClick={approve} data-testid="draft-approve-button" className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-bold flex items-center gap-1"><Check size={16} /> Approve & publish</button>
        </div>
      </div>
    </Panel>
  );
};

const GuideGenerator = () => {
  const qc = useQueryClient();
  const [cat, setCat] = useState("");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const cats = useQuery({ queryKey: ["a-categories"], queryFn: () => api.get("/admin/categories").then((r) => r.data) });
  const guides = useQuery({ queryKey: ["ai-guides"], queryFn: () => api.get("/admin/ai/guides").then((r) => r.data) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["ai-guides"] }); qc.invalidateQueries({ queryKey: ["a-posts"] }); };
  const generate = async () => {
    setBusy(true);
    const t = toast.loading("Writing guide draft (about 30-60 seconds)…");
    try { await api.post("/admin/ai/guide", { category_id: cat, topic }); toast.success("Guide draft ready below", { id: t }); setTopic(""); refresh(); }
    catch (e) { toast.error(errMsg(e), { id: t }); } finally { setBusy(false); }
  };
  const publish = async (g) => { try { await api.post(`/admin/posts/${g.id}/publish`); toast.success("Guide published"); refresh(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async (g) => {
    if (!window.confirm("Delete this draft?")) return;
    try { await api.delete(`/admin/posts/${g.id}`); toast.success("Draft deleted"); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel title="AI buying guides" testid="guide-generator-panel">
      <p className="text-xs text-slate-500 mb-3">The AI only references real published deals in the chosen category and is told not to invent specs, ratings or "best" claims. Guides stay as drafts until you publish. Edit wording in Catalog → Guides & blog.</p>
      <div className="flex flex-wrap gap-2">
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border border-slate-200 px-3 text-sm" data-testid="guide-category-select">
          <option value="">Choose category…</option>
          {(cats.data || []).map((c) => <option key={c.id} value={c.id}>{c.parent_id ? "— " : ""}{c.name}</option>)}
        </select>
        <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Topic (optional) e.g. Best value headphones under ₹5,000" className="h-10 flex-1 min-w-[240px] rounded-md border border-slate-200 px-3 text-sm" data-testid="guide-topic-input" />
        <button disabled={!cat || busy} onClick={generate} data-testid="guide-generate-button" className="h-10 px-4 rounded-lg bg-amber-500 hover:bg-amber-600 text-white font-bold disabled:opacity-50 flex items-center gap-2"><Sparkles size={16} /> {busy ? "Writing…" : "Draft guide"}</button>
      </div>
      {(guides.data || []).map((g) => (
        <details key={g.id} className="mt-4 border border-slate-200 rounded-lg" data-testid={`guide-draft-${g.id}`}>
          <summary className="cursor-pointer p-3 flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold">{g.title}</span>
            <span className="text-xs text-slate-500">{fmtDateTime(g.created_at)} · cost {inr(g.cost_inr)} · {g.deal_ids.length} deals linked</span>
          </summary>
          <div className="p-4 border-t border-slate-100 space-y-3">
            {g.missing_info?.length > 0 && <p className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900"><b>Missing information:</b> {g.missing_info.join(", ")}</p>}
            <p className="text-sm text-slate-600 italic">{g.excerpt}</p>
            <div className="max-h-80 overflow-y-auto bg-slate-50 rounded p-3"><Markdown text={g.body} /></div>
            <div className="flex flex-wrap justify-end gap-2">
              <LinkIdeas postId={g.id} />
              <button onClick={() => remove(g)} className="px-3 py-1.5 rounded-lg border text-sm" data-testid="guide-delete-button">Delete</button>
              <button onClick={() => publish(g)} className="px-4 py-1.5 rounded-lg bg-emerald-600 text-white font-bold text-sm" data-testid="guide-publish-button">Publish guide</button>
            </div>
          </div>
        </details>
      ))}
    </Panel>
  );
};

export default function ContentPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["drafts"], queryFn: () => api.get("/admin/ai/drafts").then((r) => r.data) });
  const integ = useQuery({ queryKey: ["integrations"], queryFn: () => api.get("/admin/integrations").then((r) => r.data) });
  const done = () => { qc.invalidateQueries({ queryKey: ["drafts"] }); qc.invalidateQueries({ queryKey: ["a-deals"] }); };
  const ai = integ.data?.ai;
  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <PageTitle title="AI Content review" sub="AI drafts only use facts from the deal record. Nothing is published or indexed until a person approves it. Generate drafts from the Deals page (✦ icon) or the 'Generate AI content' automation." />
      {ai && <p className="text-sm text-slate-600" data-testid="ai-provider-status">Provider: <b>{ai.provider}</b> · model <b>{ai.model}</b> · {ai.configured ? <span className="text-emerald-700 font-semibold">key configured</span> : <span className="text-red-600 font-semibold">no key - AI paused</span>}</p>}
      <GuideGenerator />
      <ComparisonGenerator />
      <CategoryIntroGenerator />
      <h2 className="text-xl font-bold pt-2">Deal content drafts</h2>
      {(data || []).map((d) => <DraftCard key={d.id} d={d} onDone={done} />)}
      {data && !data.length && <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500 bg-white" data-testid="no-drafts">No drafts waiting for review.</div>}
    </div>
  );
}
